import React, { useEffect, useState, useRef, useImperativeHandle, ReactNode, } from "react";
import { createPortal, } from "react-dom";

// --- TIPAGENS ---
export type PopoverOrigin = {
  vertical: "top" | "center" | "bottom" | number;
  horizontal: "left" | "center" | "right" | number;
};

export type PopoverPosition = {
  top: number;
  left: number;
};

export type PopoverReference = "anchorEl" | "anchorPosition" | "none";

export type PopoverActions = {
  updatePosition(): void;
};

// Substituindo todas as interfaces por types, conforme solicitado
export type PopoverProps = Omit<React.HTMLAttributes<HTMLDivElement>, "children"> & {
  action?: React.Ref<PopoverActions>;
  anchorEl?: Element | null | ((element: Element) => Element);
  anchorOrigin?: PopoverOrigin;
  anchorPosition?: PopoverPosition;
  anchorReference?: PopoverReference;
  children?: ReactNode;
  container?: Element | null;
  elevation?: number;
  getContentAnchorEl?: null | ((element: Element) => Element);
  marginThreshold?: number;
  onClose?: (event: React.MouseEvent | React.KeyboardEvent, reason: "backdropClick" | "escapeKeyDown") => void;
  open: boolean;
  PaperProps?: React.HTMLAttributes<HTMLDivElement>;
  transformOrigin?: PopoverOrigin;
};

// --- FUNÇÕES AUXILIARES DE CÁLCULO ---
const getOffsetTop = (rect: DOMRect | PopoverPosition, vertical: PopoverOrigin["vertical"]): number => {
  if (typeof vertical === "number") return vertical;
  const height = "height" in rect ? rect.height : 0;

  if (vertical === "center") return height / 2;
  if (vertical === "bottom") return height;

  return 0; // top
};

const getOffsetLeft = (rect: DOMRect | PopoverPosition, horizontal: PopoverOrigin["horizontal"]): number => {
  if (typeof horizontal === "number") return horizontal;
  const width = "width" in rect ? rect.width : 0;

  if (horizontal === "center") return width / 2;
  if (horizontal === "right") return width;

  return 0; // left
};

const getElevationClass = (elevation: number): string => {
  if (elevation === 0) return "shadow-none";
  if (elevation < 4) return "shadow-sm";
  if (elevation < 8) return "shadow-md";
  if (elevation < 16) return "shadow-lg";
  if (elevation < 20) return "shadow-xl";

  return "shadow-2xl";
};

// --- COMPONENTE PRINCIPAL ---
export default function Popover({
  action,
  anchorEl,
  anchorOrigin = { vertical: "top", horizontal: "left" },
  anchorPosition,
  anchorReference = "anchorEl",
  children,
  container,
  elevation = 8,
  marginThreshold = 16,
  onClose,
  open,
  PaperProps = {},
  transformOrigin = { vertical: "top", horizontal: "left" },
  className = "",
  ...props
}: PopoverProps): JSX.Element {
  const { className: paperClassName, style: paperStyle, ...paperRest } = PaperProps;
  const popoverRef = useRef<HTMLDivElement>(null);
  const [styles, setStyles] = useState<React.CSSProperties>({ top: 0, left: 0, opacity: 0 });

  // Lógica de cálculo de posição
  const updatePosition = () => {
    if (!popoverRef.current) return;

    const popoverRect = popoverRef.current.getBoundingClientRect();
    let anchorPointX = 0;
    let anchorPointY = 0;

    // Se a referência for um elemento do DOM
    if (anchorReference === "anchorEl" && anchorEl instanceof Element) {
      const anchorRect = anchorEl.getBoundingClientRect();

      anchorPointX = anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal) + window.scrollX;
      anchorPointY = anchorRect.top + getOffsetTop(anchorRect, anchorOrigin.vertical) + window.scrollY;
    }
    // Se a referência for uma posição arbitrária (ex: clique com botão direito)
    else if (anchorReference === "anchorPosition" && anchorPosition) {
      anchorPointX = anchorPosition.left + window.scrollX;
      anchorPointY = anchorPosition.top + window.scrollY;
    }

    const transformOffsetX = getOffsetLeft(popoverRect, transformOrigin.horizontal);
    const transformOffsetY = getOffsetTop(popoverRect, transformOrigin.vertical);

    let top = anchorPointY - transformOffsetY;
    let left = anchorPointX - transformOffsetX;

    // Aplica o marginThreshold para evitar que o Popover saia da tela
    const maxTop = window.scrollY + window.innerHeight - popoverRect.height - marginThreshold;
    const maxLeft = window.scrollX + window.innerWidth - popoverRect.width - marginThreshold;

    top = Math.max(window.scrollY + marginThreshold, Math.min(top, maxTop));
    left = Math.max(window.scrollX + marginThreshold, Math.min(left, maxLeft));

    setStyles({ top: `${top}px`, left: `${left}px`, opacity: 1 });
  };

  // Expõe o método updatePosition para a ref 'action', permitindo atualização imperativa
  useImperativeHandle(action, () => ({ updatePosition }));

  // Observadores de estado e redimensionamento
  useEffect(() => {
    if (!open) {
      setStyles((prev) => ({ ...prev, opacity: 0 }));

      return;
    }

    requestAnimationFrame(updatePosition);

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorEl, anchorReference, anchorPosition, anchorOrigin, transformOrigin, marginThreshold]);

  // Handler para a tecla Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open && onClose) {
        onClose(event as unknown as React.KeyboardEvent, "escapeKeyDown");
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Bloqueia o scroll do body (comportamento nativo do Modal do MUI que o Popover herda)
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open && styles.opacity === 0) return <></>;

  const popoverContent = (
    <div className={`fixed inset-0 z-50 ${className}`} {...props}>
      {/* Backdrop */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-transparent"
        onClick={(e) => onClose && onClose(e, "backdropClick")}
      />

      {/* Paper (Container do Conteúdo) */}
      <div
        ref={popoverRef}
        className={`absolute rounded-md bg-white transition-opacity duration-200 ${getElevationClass(elevation)} ${paperClassName || ""}`}
        style={{ ...styles, ...paperStyle }}
        {...paperRest}
      >
        {children}
      </div>
    </div>
  );

  const mountNode = container || (typeof document !== "undefined" ? document.body : null);

  if (mountNode) {
    return createPortal(popoverContent, mountNode);
  }

  return <></>;
}